import { useEffect } from "react";
import { setSEO } from "../lib/data/seo";
import { RevealLines, Reveal } from "../components/motion/Reveal";
import SpotlightImage from "../components/site/SpotlightImage";
import EditorialMarquee from "../components/site/EditorialMarquee";
import { Link } from "react-router-dom";

export default function About() {
  useEffect(() => {
    setSEO({
      title: "About British Columbia Security Services — Provincial Operator Since 2011",
      description:
        "A single provincial security operator built for British Columbia — mobile patrol, mining, marine, corporate, retail, and event teams under one dispatch since 2011.",
      keywords: [
        "about British Columbia Security Services",
        "BC security operator",
        "provincial security company",
      ],
      canonical: `${window.location.origin}/about`,
    });
  }, []);

  return (
    <div className="pt-24">
      <section className="pt-16 md:pt-24 pb-16 bg-[#F4F4F0]" data-testid="about-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Who We Are · Since 2011
          </div>
          <RevealLines
            lines={["Built for British Columbia.", "Only British Columbia."]}
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.9] text-[#060913]"
          />
        </div>
      </section>

      <EditorialMarquee items={["ONE PROVINCE", "ONE DISPATCH", "ZERO COMPROMISES", "SINCE 2011"]} />

      <section className="bg-[#F4F4F0] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6 md:col-start-2">
            <Reveal>
              <p className="text-xl md:text-2xl leading-relaxed text-[#060913]">
                We opened in Vancouver in 2011 with a stubborn idea — that British
                Columbia deserved a security company that could staff a mine gatehouse
                in the Kootenays, patrol a Yaletown high-rise, and secure a Whistler
                film shoot without handing off to sub-contractors.
              </p>
              <p className="mt-6 text-lg text-[#5C5F66] leading-relaxed">
                Today, we run a single provincial operations desk with over 400
                licensed officers deployed across 50+ BC cities. We pick the work
                deliberately, we hire deliberately, and we report on every patrol —
                because clients who need real security shouldn't have to accept
                anything less.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-4">
            <SpotlightImage
              src="https://images.unsplash.com/photo-1652739758426-56a564265f9e"
              alt="Security operations centre"
              caption="Operations · Vancouver"
              className="aspect-[3/4]"
            />
          </div>
        </div>
      </section>

      {/* NUMBERED PRINCIPLES */}
      <section className="bg-white py-24 md:py-32 border-y border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            The way we run
          </div>
          <h2 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#060913] max-w-4xl">
            Four operating principles.
          </h2>
          <div className="mt-16 border-t border-black/10">
            {[
              {
                n: "01",
                t: "Retention over churn.",
                b: "The industry-average officer tenure is eleven months. Ours is closer to four years. We pay above scale, train in-house, and promote from within — the people watching your site are the same people watching it next year.",
              },
              {
                n: "02",
                t: "Documentation as culture.",
                b: "Every patrol logged. Every incident photographed and timestamped. Every shift's paperwork indexed and searchable. If you can't audit it, it didn't happen.",
              },
              {
                n: "03",
                t: "One dispatch, one accountability.",
                b: "We refuse to sub-contract. When you sign a BC-wide contract with us, every officer wearing our uniform works directly for us — no matter which corner of the province they operate from.",
              },
              {
                n: "04",
                t: "Slow scale, deep bench.",
                b: "We grow only when we can staff. Not the other way around. That's why we can send trained officers to a mine site or a mall opening the same week you call.",
              },
            ].map((p, i) => (
              <Reveal key={p.n} delay={i * 0.05}>
                <div className="grid grid-cols-12 gap-6 py-14 border-b border-black/10">
                  <div className="col-span-3 md:col-span-2 font-mono text-6xl md:text-8xl text-[#D92323] leading-none">
                    {p.n}
                  </div>
                  <div className="col-span-9 md:col-span-10">
                    <h3 className="font-display text-3xl md:text-5xl tracking-tight leading-tight text-[#060913]">
                      {p.t}
                    </h3>
                    <p className="mt-4 text-[#5C5F66] text-base md:text-lg max-w-2xl leading-relaxed">
                      {p.b}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#D92323] text-white py-20 md:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase opacity-70">
              Talk to us
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[0.95] tracking-tight">
              Serious about security in BC? So are we.
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="about-contact-cta"
              className="inline-flex items-center gap-3 border border-white bg-white text-[#060913] px-6 py-4 font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-transparent hover:text-white transition-colors"
            >
              Contact Us →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
